import React, { useEffect, useLayoutEffect, useRef } from "react";
import { BrowserRouter as Router, matchPath, Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UsageProvider } from './contexts/UsageContext';
import { StripeProvider, useStripe } from './contexts/StripeContext';
import { ClipsProvider } from './contexts/ClipsContext';
import {
  Navbar,
  type AppNavigationPage,
} from './components/layout/Navbar';
import { LandingPage } from './pages/index';
import { TopClipAutoShell } from './features/topclip-auto/TopClipAutoShell';
import {
  LegacyCreateJobRedirect,
  ProtectedRoute,
  TOPCLIP_AUTO_ROUTE_PATHS,
} from './features/topclip-auto/topclipAutoRoutes';
import Footer from './components/layout/Footer'
import DashboardPage from './pages/dashboard/index';
import GoogleSignIn from './pages/sign-in/index';
import { SuccessPage } from './pages/success/index';
import { ClipsLibraryPage } from './pages/clips/index';
import TermsOfService from './pages/terms/index';
import PrivacyPolicy from './pages/privacy/index';
import { InitialAppShell } from './components/layout/InitialAppShell';
import { processFacebookCookies, initializeFacebookPixelWithEmail } from './utils/facebookCookies';
import { initializeSnapchatTracking, processSnapchatCookies } from './utils/snapchatCookies';
import { getPageViewEventId } from './utils/eventIdManager';
import { initializeChatbase } from './utils/chatbase';
import {
  oauthErrorMessage,
  signInPathForDestination,
  storeAuthRedirectDestination,
} from './lib/authRedirect';
import {
  clearLandingSourceIntent,
  storeLandingSourceIntent,
} from './lib/landingSourceIntent';

export { LegacyCreateJobRedirect, ProtectedRoute, TOPCLIP_AUTO_ROUTE_PATHS };

export const isCanonicalTopClipAutoPath = (pathname: string): boolean =>
  matchPath(
    { path: TOPCLIP_AUTO_ROUTE_PATHS.create, caseSensitive: false, end: true },
    pathname
  ) !== null ||
  matchPath(
    { path: TOPCLIP_AUTO_ROUTE_PATHS.job, caseSensitive: false, end: true },
    pathname
  ) !== null;

export const getAppNavigationPage = (
  pathname: string
): AppNavigationPage | null => {
  if (isCanonicalTopClipAutoPath(pathname)) {
    return "create";
  }

  if (
    matchPath(
      { path: "/clips", caseSensitive: false, end: true },
      pathname
    )
  ) {
    return "clips";
  }

  if (
    matchPath(
      { path: "/dashboard", caseSensitive: false, end: true },
      pathname
    )
  ) {
    return "account";
  }

  return null;
};

export const AppContent: React.FC = () => {
  const { user, loading, signInWithGoogle, setNavigate } = useAuth();
  const { checkUserSubscription } = useStripe();
  const navigate = useNavigate();
  const location = useLocation();
  const isTopClipAutoRoute = isCanonicalTopClipAutoPath(location.pathname);
  const activeAppPage = getAppNavigationPage(location.pathname);
  const startRoutingRef = useRef(false);

  useLayoutEffect(() => {
    // New pages start at the top; landing anchors retain their explicit target.
    if (location.pathname === '/' && location.hash) return;
    // This project's older DOM types omit the browser-supported instant value.
    // Explicit behavior avoids inheriting the landing page's smooth scrolling.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    processFacebookCookies();
    initializeSnapchatTracking();
    processSnapchatCookies();
  }, [location.search]);

  useEffect(() => {
    if (user?.email) {
      if (typeof (window as any).snaptr !== 'undefined') {
        (window as any).snaptr('init', '43295bce-d3e5-416f-a288-cc47fd729a76', {
          user_email: user.email
        });
      }

      initializeFacebookPixelWithEmail(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const loadChatbaseForPaidUsers = async () => {
      if (isTopClipAutoRoute || !user) {
        return;
      }

      try {
        const subscriptionStatus = await checkUserSubscription();

        if (subscriptionStatus.has_subscription) {
          initializeChatbase();
        }
      } catch (error) {
        console.error('Failed to check subscription for Chatbase:', error);
      }
    };

    loadChatbaseForPaidUsers();
  }, [isTopClipAutoRoute, user, checkUserSubscription]);

  // Track page views with deduplication ID on route changes
  useEffect(() => {
    if (typeof (window as any).snaptr !== 'undefined') {
      const eventId = getPageViewEventId();

      (window as any).snaptr('track', 'PAGE_VIEW', {
        client_dedup_id: eventId
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('📸 Snap Pixel PAGE_VIEW tracked with client_dedup_id:', eventId, 'for', location.pathname);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate, setNavigate]);

  const handleStartClipping = async (sourceUrl?: string) => {
    if (loading || startRoutingRef.current) return;

    if (sourceUrl !== undefined) {
      const intent = storeLandingSourceIntent(sourceUrl, user?.id ?? null);
      if (!intent) return;
    } else {
      clearLandingSourceIntent();
    }

    startRoutingRef.current = true;
    try {
      if (!user) {
        storeAuthRedirectDestination('/create-clips');
        const result = await signInWithGoogle();
        if (result.error) {
          navigate(signInPathForDestination('/create-clips'));
        }
        return;
      }
      navigate('/create-clips');
    } finally {
      startRoutingRef.current = false;
    }
  };

  const handleShowPricing = () => {
    // If we're already on the home page, just scroll to pricing
    if (location.pathname === '/') {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home with pricing hash
      navigate('/#pricing-section');
    }
  };

  // Handle live landing anchors after same-page or cross-route navigation.
  useEffect(() => {
    const sectionId = location.hash.replace(/^#/, '');
    if (!sectionId || location.pathname !== '/') return;
    const timer = window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const handleShowDashboard = () => {
    navigate(user ? '/dashboard' : signInPathForDestination('/dashboard'));
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleShowSignIn = async () => {
    clearLandingSourceIntent();
    try {
      const result = await signInWithGoogle();
      if (result.error) {
        navigate(signInPathForDestination('/create-clips'));
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      navigate(signInPathForDestination('/create-clips'));
    }
  };

  const handleShowSignUp = async () => {
    await handleShowSignIn();
  };

  if (loading) {
    return <InitialAppShell />;
  }

  const oauthError = oauthErrorMessage(location.search, location.hash);
  if (oauthError && location.pathname !== '/sign-in') {
    return (
      <Navigate
        replace
        to={`/sign-in${location.search}${location.hash}`}
      />
    );
  }

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-0 bg-white" />

      <div className="relative z-10">
        <Navbar
          onStartClipping={handleStartClipping}
          onShowPricing={handleShowPricing}
          onShowDashboard={handleShowDashboard}
          onShowClips={() => navigate('/clips')}
          onGoHome={handleBackToHome}
          onSignIn={handleShowSignIn}
          onSignUp={handleShowSignUp}
          activeAppPage={activeAppPage}
        />
        
        <Routes>
          <Route path="/" element={<LandingPage onStartClipping={handleStartClipping} />} />
          <Route
            path="/getting-started"
            element={<Navigate replace to={TOPCLIP_AUTO_ROUTE_PATHS.create} />}
          />
          <Route path={TOPCLIP_AUTO_ROUTE_PATHS.create} element={
            <ProtectedRoute>
              <TopClipAutoShell />
            </ProtectedRoute>
          } />
          <Route path={TOPCLIP_AUTO_ROUTE_PATHS.job} element={
            <ProtectedRoute>
              <TopClipAutoShell />
            </ProtectedRoute>
          } />
          <Route path={TOPCLIP_AUTO_ROUTE_PATHS.legacyDevCreate} element={
            <Navigate replace to={TOPCLIP_AUTO_ROUTE_PATHS.create} />
          } />
          <Route path={TOPCLIP_AUTO_ROUTE_PATHS.legacyDevJob} element={
            <LegacyCreateJobRedirect />
          } />
          <Route path={TOPCLIP_AUTO_ROUTE_PATHS.legacyAiCreate} element={
            <Navigate replace to={TOPCLIP_AUTO_ROUTE_PATHS.create} />
          } />
          <Route path="/sign-in" element={<GoogleSignIn onBackToHome={handleBackToHome} />} />
          <Route path="/success" element={<SuccessPage onBack={handleBackToHome} onStartClipping={() => void handleStartClipping()} onShowDashboard={() => navigate(user ? '/dashboard' : signInPathForDestination('/dashboard'))} />} />
          <Route path="/terms" element={<TermsOfService onBack={handleBackToHome} />} />
          <Route path="/privacy" element={<PrivacyPolicy onBack={handleBackToHome} />} />
          <Route path="/blog" element={<Navigate replace to="/" />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <ClipsProvider>
                <DashboardPage
                  onBack={handleBackToHome}
                  onStartClipping={() => void handleStartClipping()}
                  onShowClips={() => navigate('/clips')}
                  onShowPricing={handleShowPricing}
                />
              </ClipsProvider>
            </ProtectedRoute>
          } />
          <Route path="/clips" element={
            <ProtectedRoute>
              <ClipsProvider>
                <ClipsLibraryPage
                  onBack={handleBackToHome}
                  onStartClipping={() => void handleStartClipping()}
                />
              </ClipsProvider>
            </ProtectedRoute>
          } />
        </Routes>
        
        {!isTopClipAutoRoute && (
          <Footer
            onStartClipping={handleStartClipping}
            onShowSignUp={handleShowSignUp}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
          />
        )}
      </div>
    </>
  );
};

export const AppRuntime: React.FC = () => {
  const location = useLocation();
  const effectsEnabled = !isCanonicalTopClipAutoPath(location.pathname);

  return (
    <UsageProvider effectsEnabled={effectsEnabled}>
      <StripeProvider>
        <AppContent />
      </StripeProvider>
    </UsageProvider>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRuntime />
      </AuthProvider>
    </Router>
  );
}

export default App;
